<!-- views/SearchResultsView.vue -->
<template>
  <div class="search-results">
    <div class="search-container">
      <!-- Search Header -->
      <div class="search-header">
        <h1>Search Results</h1>
        <div class="search-bar">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search for courses, creators, topics..."
            @keyup.enter="performSearch"
          />
          <button @click="performSearch" class="search-btn">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters">
        <button
          v-for="filter in filters"
          :key="filter.id"
          class="filter-btn"
          :class="{ active: activeFilter === filter.id }"
          @click="activeFilter = filter.id"
        >
          {{ filter.icon }} {{ filter.label }}
        </button>
      </div>

      <!-- Results -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Searching...</p>
      </div>

      <div v-else-if="results.length === 0" class="empty-state">
        <svg
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <h3>No results found</h3>
        <p>Try adjusting your search or filters</p>
      </div>

      <div v-else class="results-grid">
        <div
          v-for="result in results"
          :key="result.id"
          class="result-item"
          @click="navigateTo(result)"
        >
          <!-- Course Result -->
          <div v-if="result.type === 'course'" class="course-result">
            <img
              :src="result.thumbnail || '/default-thumbnail.jpg'"
              :alt="result.title"
            />
            <div class="result-info">
              <h4>{{ result.title }}</h4>
              <p>{{ result.description?.substring(0, 150) }}...</p>
              <div class="result-meta">
                <span>👨‍🏫 {{ result.creatorName }}</span>
                <span>⭐ {{ result.rating?.toFixed(1) || "New" }}</span>
                <span
                  >💰 {{ result.price > 0 ? `$${result.price}` : "Free" }}</span
                >
              </div>
            </div>
          </div>

          <!-- User Result -->
          <div v-else-if="result.type === 'user'" class="user-result">
            <img
              :src="
                result.photoURL ||
                `https://ui-avatars.com/api/?name=${result.displayName}&background=FF8000&color=fff`
              "
              :alt="result.displayName"
            />
            <div class="result-info">
              <h4>{{ result.displayName }}</h4>
              <p>{{ result.bio || "Course Creator" }}</p>
              <div class="result-meta">
                <span>📚 {{ result.courses }} courses</span>
                <span>👥 {{ result.followers }} followers</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="hasMore" class="load-more">
        <button @click="loadMore" :disabled="loadingMore" class="btn-secondary">
          {{ loadingMore ? "Loading..." : "Load More" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import api from "@/services/api";

const route = useRoute();
const router = useRouter();

const searchQuery = ref(route.query.q || "");
const results = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const hasMore = ref(true);
const page = ref(1);
const activeFilter = ref("all");

const filters = [
  { id: "all", label: "All", icon: "🌐" },
  { id: "courses", label: "Courses", icon: "📚" },
  { id: "users", label: "Creators", icon: "👤" },
  { id: "posts", label: "Posts", icon: "📝" },
];

onMounted(() => {
  if (route.query.q) {
    performSearch();
  }
});

watch(
  () => route.query.q,
  (newQuery) => {
    if (newQuery) {
      searchQuery.value = newQuery;
      performSearch();
    }
  },
);

const performSearch = async () => {
  if (!searchQuery.value.trim()) return;

  loading.value = true;
  page.value = 1;

  try {
    const response = await api.get("/search", {
      params: {
        q: searchQuery.value,
        type: activeFilter.value !== "all" ? activeFilter.value : undefined,
        page: page.value,
        limit: 20,
      },
    });
    results.value = response.data.data;
    hasMore.value = response.data.pagination.hasMore;
  } catch (error) {
    console.error("Search error:", error);
  } finally {
    loading.value = false;
  }
};

const loadMore = async () => {
  loadingMore.value = true;
  page.value++;

  try {
    const response = await api.get("/search", {
      params: {
        q: searchQuery.value,
        type: activeFilter.value !== "all" ? activeFilter.value : undefined,
        page: page.value,
        limit: 20,
      },
    });
    results.value = [...results.value, ...response.data.data];
    hasMore.value = response.data.pagination.hasMore;
  } catch (error) {
    console.error("Load more error:", error);
  } finally {
    loadingMore.value = false;
  }
};

const navigateTo = (result) => {
  if (result.type === "course") {
    router.push(`/course/${result.id}`);
  } else if (result.type === "user") {
    router.push(`/profile/${result.id}`);
  }
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.search-results {
  margin-top: 72px;
  min-height: 100vh;
  background: #f0f2f5;
  padding: 24px;
}

.search-container {
  max-width: 1200px;
  margin: 0 auto;
}

.search-header {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  h1 {
    margin: 0 0 16px 0;
    color: #1a1a1a;
  }

  .search-bar {
    display: flex;
    gap: 0;

    input {
      flex: 1;
      padding: 14px 20px;
      border: 2px solid #e9ecef;
      border-radius: 12px 0 0 12px;
      font-size: 16px;

      &:focus {
        border-color: $primary-orange;
        outline: none;
      }
    }

    .search-btn {
      padding: 14px 24px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 0 12px 12px 0;
      cursor: pointer;

      &:hover {
        background: darken($primary-orange, 10%);
      }
    }
  }
}

.filters {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .filter-btn {
    padding: 8px 16px;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $primary-orange;
    }

    &.active {
      background: $primary-orange;
      color: #fff;
      border-color: $primary-orange;
    }
  }
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e9ecef;
    border-top: 4px solid $primary-orange;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }

  p {
    margin-top: 16px;
    color: #868e96;
  }
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 12px;

  svg {
    color: #adb5bd;
    margin-bottom: 16px;
  }

  h3 {
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  p {
    color: #868e96;
  }
}

.results-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.result-item {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

.course-result,
.user-result {
  display: flex;
  gap: 16px;

  img {
    width: 120px;
    height: 80px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }

  .result-info {
    flex: 1;

    h4 {
      margin: 0 0 4px 0;
      color: #1a1a1a;
    }

    p {
      margin: 0 0 8px 0;
      color: #495057;
      font-size: 14px;
    }

    .result-meta {
      display: flex;
      gap: 16px;
      font-size: 13px;
      color: #868e96;
    }
  }
}

.user-result {
  img {
    width: 60px;
    height: 60px;
    border-radius: 50%;
  }
}

.load-more {
  text-align: center;
  margin-top: 24px;

  .btn-secondary {
    padding: 12px 32px;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    color: #495057;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: $primary-orange;
      color: $primary-orange;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
